import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import SeoManager from "@/components/SeoManager";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Feedback from "@/pages/Feedback";
import AdminApp from "@/app/AdminApp";
import ServiceArea from "@/pages/ServiceArea";
import OfficeVending from "@/pages/OfficeVending";
import ApartmentVending from "@/pages/ApartmentVending";
import MedicalVending from "@/pages/MedicalVending";
import TownPage from "@/pages/TownPage";
import NotFound from "@/pages/NotFound";
import { Toaster } from "@/components/ui/sonner";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    // allowNestedScroll lets scrollable panels and dialogs (operator panel) scroll natively.
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, allowNestedScroll: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <SeoManager />
        <div className="min-h-screen bg-vortex-ink text-slate-100 font-body antialiased overflow-x-clip">
          <div className="noise-overlay" aria-hidden="true" />
          <Routes>
            {/* The private workspace has its own layout (sidebar, sign-in) instead of the public header and footer. */}
            <Route path="/admin/*" element={<AdminApp />} />
            <Route path="*" element={<PublicSite />} />
          </Routes>
          <Toaster position="bottom-right" theme="dark" />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

const PublicSite = () => (
  <>
    <SiteNav />
    <main>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/service-area" element={<ServiceArea />} />
        <Route path="/office-vending" element={<OfficeVending />} />
        <Route path="/apartment-building-vending" element={<ApartmentVending />} />
        <Route path="/medical-office-vending" element={<MedicalVending />} />
        <Route path="/areas/:slug" element={<TownPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
    <SiteFooter />
  </>
);

export default App;
